import * as React from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import { composeRefs } from '../../internals/composeRefs';
import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Slot } from '../../internals/Slot';
import { useControllable } from '../../internals/useControllable';
import { rem } from '../../tokens/spacing';

export type TabsVariant = 'default' | 'primary' | 'fit-content';
export type TabsSize = 'md' | 'lg';

/** Legacy flat tabs API (items + controlled index). */
export interface TabItem {
  name: string;
  content?: React.ReactNode;
  disabled?: boolean;
}

export interface TabsLegacyProps extends React.HTMLAttributes<HTMLDivElement> {
  items: TabItem[];
  tabIndex: number;
  setTabIndex: (value: number) => void;
  variant?: TabsVariant;
  size?: TabsSize;
  centered?: boolean;
  isVertical?: boolean;
  hasTabContent?: boolean;
  semanticId?: string;
}

const TabHeaderContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$verticalList',
})<{ $verticalList?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: ${({ $verticalList }) => ($verticalList ? 0 : rem(24))};
  box-sizing: border-box;

  ${({ $verticalList }) =>
    $verticalList
      ? css`
          width: max-content;
          max-width: 100%;
          align-self: flex-start;
          padding-inline-end: ${rem(12)};
        `
      : ''}
`;

const TabsHolder = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$variant', '$orientation'].includes(String(prop)),
})<{ $variant: TabsVariant; $orientation: 'horizontal' | 'vertical' }>`
  display: flex;
  border-radius: var(--ui-radius-md, 6px);
  padding: 3px;
  gap: 0.2rem;
  box-sizing: border-box;

  &.tab-vertical {
    background-color: var(--ui-background-muted);
  }

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          flex-direction: column;
          flex-shrink: 0;
          width: max-content;
          max-width: 100%;
          align-self: flex-start;
        `
      : css`
          flex-direction: row;
          width: max-content;
          max-width: 100%;

          &[data-overflows='true'] {
            flex-direction: column;
            width: 100%;
          }
        `}

  &.centered {
    margin: 0 auto;
  }

  ${({ $variant }) => {
    if ($variant === 'primary') {
      return css`
        background-color: var(--ui-surface);
      `;
    }
    return css`
      background-color: var(--ui-tab-inactive-bg, var(--ui-background-muted));
    `;
  }};
`;

const StyledTab = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$variant', '$size'].includes(String(prop)),
})<{ $variant: TabsVariant; $size: TabsSize }>`
  font-weight: 500;
  text-align: left;
  position: relative;
  z-index: 0;
  cursor: pointer;

  border-radius: var(--ui-radius-md, ${rem(6)});
  transition: all 0.2s ease-in-out;
  padding: ${({ $size }) =>
    $size === 'lg' ? `${rem(11)} ${rem(14)}` : `${rem(10)} ${rem(17)}`};

  .underline-slider {
    height: 44px;
    background: var(--ui-surface);
    border-radius: var(--ui-radius-md, 6px);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: -1;

    &.underline-slider-small {
      height: 34px;
    }
  }

  ${({ $variant }) => {
    if ($variant === 'primary') {
      return css`
        &:hover span,
        &.active span,
        &[data-state='active'] span {
          color: var(--ui-interactive);
          font-weight: 500;
        }

        &:hover .underline-slider,
        &.active .underline-slider,
        &[data-state='active'] .underline-slider {
          background-color: var(--ui-interactive-light);
          box-shadow: ${rem(0)} ${rem(2)} ${rem(3)} var(--ui-shadow-default);
        }
      `;
    }

    return css`
      &:hover,
      &.active,
      &[data-state='active'] {
        .underline-slider {
          background-color: var(--ui-surface);
          box-shadow: ${rem(0)} ${rem(2)} ${rem(3)} var(--ui-shadow-default);
        }

        span {
          color: var(--ui-interactive);
          font-weight: 500;
        }
      }
    `;
  }};

  &[data-disabled='true'] {
    pointer-events: none;
    opacity: 0.6;
  }
`;

const TabContent = styled.div`
  max-width: 100%;
  margin: 0;
`;

/**
 * Matches production customer-ui `src/utils/global.tsx` `toCamelCase`.
 * Used for tab trigger DOM ids (legacy `StyledTab id={toCamelCase(items.name)}`).
 */
export function tabLabelToProductionDomId(str: string | undefined | null): string {
  if (str == null || str === '') {
    return 'tab';
  }
  return str
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

function compoundTriggerAndPanelIds(value: string, domIdPrefix: string | undefined) {
  const raw = tabLabelToProductionDomId(value);
  const triggerId = domIdPrefix ? `${domIdPrefix}-${raw}` : raw;
  const panelId = `${triggerId}-tabpanel`;
  return { triggerId, panelId };
}

/** @deprecated Prefer compound `Tabs.Root` / `Tabs.List` / `Tabs.Trigger` / `Tabs.Content`. */
export const TabsLegacy = React.forwardRef<HTMLDivElement, TabsLegacyProps>(function TabsLegacy(
  {
    items,
    tabIndex,
    setTabIndex,
    variant = 'default',
    size = 'md',
    centered = false,
    isVertical = false,
    hasTabContent = false,
    semanticId,
    className,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <div ref={ref} {...semanticProps} {...rest}>
      <TabHeaderContainer className={className}>
        <TabsHolder
          $variant={variant}
          $orientation={isVertical ? 'vertical' : 'horizontal'}
          className={`${isVertical ? 'tab-vertical' : ''} ${centered ? 'centered' : ''}`}
        >
          {items?.map((item, i) => {
            const isActive = i === tabIndex;
            return (
              <StyledTab
                key={i}
                id={tabLabelToProductionDomId(item.name)}
                className={`${item.disabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`}
                onClick={() => setTabIndex(i)}
                $variant={variant}
                $size={size}
              >
                <span
                  style={{
                    fontSize: size === 'lg' ? rem(16) : rem(12),
                    lineHeight: size === 'lg' ? rem(22) : rem(14),
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {item.name}
                </span>
                {isActive && (
                  <motion.div
                    className={`underline-slider ${size === 'md' ? 'underline-slider-small' : ''}`}
                    layoutId="underline-slider"
                  />
                )}
              </StyledTab>
            );
          })}
        </TabsHolder>
      </TabHeaderContainer>

      {hasTabContent && (
        <AnimatePresence mode="wait">
          {items?.map((item, i) =>
            tabIndex === i ? (
              <motion.section
                key={`tab-index-${tabIndex}`}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <TabContent>{item.content}</TabContent>
              </motion.section>
            ) : null,
          )}
        </AnimatePresence>
      )}
    </div>
  );
});

TabsLegacy.displayName = 'TabsLegacy';

// --- Compound Tabs ---

export interface TabsRootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  variant?: TabsVariant;
  size?: TabsSize;
  centered?: boolean;
  semanticId?: string;
  children: React.ReactNode;
  /**
   * Prepended to trigger/panel DOM ids when multiple tab widgets exist on one page.
   * Production tabs used bare `toCamelCase(tabName)` on each trigger; use this to avoid id collisions.
   */
  domIdPrefix?: string;
}

interface TabsRegistry {
  order: Set<string>;
  disabled: Map<string, boolean>;
  focusEl: Map<string, HTMLElement | null>;
  register: (value: string, disabled: boolean) => void;
  unregister: (value: string) => void;
  setFocusEl: (value: string, el: HTMLElement | null) => void;
}

interface TabsContextValue {
  value: string;
  setValue: (value: string) => void;
  baseId: string;
  domIdPrefix: string | undefined;
  orientation: 'horizontal' | 'vertical';
  variant: TabsVariant;
  size: TabsSize;
  centered: boolean;
  registry: TabsRegistry;
}

const [TabsProvider, useTabsContext] = createUIContext<TabsContextValue>('Tabs');

function createRegistry(): TabsRegistry {
  const order = new Set<string>();
  const disabled = new Map<string, boolean>();
  const focusEl = new Map<string, HTMLElement | null>();

  return {
    order,
    disabled,
    focusEl,
    register(value: string, isDisabled: boolean) {
      order.add(value);
      disabled.set(value, isDisabled);
    },
    unregister(value: string) {
      order.delete(value);
      disabled.delete(value);
      focusEl.delete(value);
    },
    setFocusEl(value: string, el: HTMLElement | null) {
      focusEl.set(value, el);
    },
  };
}

const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(function TabsRoot(
  {
    defaultValue = '',
    value: valueProp,
    onValueChange,
    orientation = 'horizontal',
    variant = 'default',
    size = 'md',
    centered = false,
    semanticId,
    domIdPrefix,
    children,
    ...rest
  },
  ref,
) {
  const baseId = React.useId().replace(/:/g, '');
  const registryRef = React.useRef<TabsRegistry>();
  if (!registryRef.current) {
    registryRef.current = createRegistry();
  }

  const [value, setValue] = useControllable<string>({
    value: valueProp,
    defaultValue,
    onChange: onValueChange,
  });

  const semanticProps = getSemanticAttributes(semanticId);

  const ctx = React.useMemo(
    () => ({
      value,
      setValue,
      baseId,
      domIdPrefix,
      orientation,
      variant,
      size,
      centered,
      registry: registryRef.current!,
    }),
    [value, setValue, baseId, domIdPrefix, orientation, variant, size, centered],
  );

  return (
    <TabsProvider value={ctx}>
      <div ref={ref} {...semanticProps} {...rest}>
        {children}
      </div>
    </TabsProvider>
  );
});

TabsRoot.displayName = 'Tabs.Root';

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { semanticId, className, children, ...rest },
  ref,
) {
  const { orientation, variant, centered } = useTabsContext();
  const semanticProps = getSemanticAttributes(semanticId);
  const isVertical = orientation === 'vertical';
  const holderRef = React.useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = React.useState(false);

  React.useEffect(() => {
    const el = holderRef.current;
    if (!el || isVertical) return;

    const check = () => {
      // Force row + unconstrained width to measure the tabs' natural inline size
      el.setAttribute('data-overflows', 'false');
      el.style.maxWidth = 'none';
      el.style.flexWrap = 'nowrap';

      const naturalWidth = el.scrollWidth;
      const containerWidth = el.parentElement!.clientWidth;

      // Restore
      el.style.maxWidth = '';
      el.style.flexWrap = '';

      const shouldOverflow = naturalWidth > containerWidth;
      el.setAttribute('data-overflows', String(shouldOverflow));
      setOverflows(shouldOverflow);
    };

    const observer = new ResizeObserver(check);
    observer.observe(el.parentElement!);
    check();
    return () => observer.disconnect();
  }, [isVertical, children]);

  return (
    <TabHeaderContainer
      ref={ref}
      className={className}
      $verticalList={isVertical}
      {...semanticProps}
      {...rest}
    >
      <TabsHolder
        ref={holderRef}
        role="tablist"
        aria-orientation={overflows ? 'vertical' : orientation}
        $variant={variant}
        $orientation={orientation}
        data-overflows={String(overflows)}
        className={`${isVertical ? 'tab-vertical' : ''} ${centered && !overflows ? 'centered' : ''}`}
      >
        {children}
      </TabsHolder>
    </TabHeaderContainer>
  );
});

TabsList.displayName = 'Tabs.List';

export interface TabsTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  disabled?: boolean;
  asChild?: boolean;
  semanticId?: string;
}

function orderedEnabledValues(registry: TabsRegistry): string[] {
  return Array.from(registry.order).filter((v) => !registry.disabled.get(v));
}

const TabsTrigger = React.forwardRef<HTMLDivElement, TabsTriggerProps>(function TabsTrigger(
  { value, disabled = false, asChild = false, semanticId, children, onClick, onKeyDown, ...rest },
  ref,
) {
  const { value: selected, setValue, baseId, domIdPrefix, orientation, variant, size, registry } =
    useTabsContext();
  const semanticProps = getSemanticAttributes(semanticId);
  const isSelected = selected === value;
  const { triggerId, panelId } = compoundTriggerAndPanelIds(value, domIdPrefix);

  React.useLayoutEffect(() => {
    registry.register(value, disabled);
    return () => registry.unregister(value);
  }, [value, disabled, registry]);

  const composedRef = React.useMemo(
    () =>
      composeRefs(ref, (node: HTMLDivElement | null) => {
        registry.setFocusEl(value, node);
      }),
    [ref, registry, value],
  );

  const focusAt = (nextValue: string) => {
    registry.focusEl.get(nextValue)?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled) return;

    const ordered = orderedEnabledValues(registry);
    const idx = ordered.indexOf(value);
    if (idx < 0) return;

    const horizontal = orientation === 'horizontal';
    const prevKey = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = horizontal ? 'ArrowRight' : 'ArrowDown';

    if (event.key === prevKey) {
      event.preventDefault();
      const next = ordered[idx - 1];
      if (next) {
        setValue(next);
        focusAt(next);
      }
    } else if (event.key === nextKey) {
      event.preventDefault();
      const next = ordered[idx + 1];
      if (next) {
        setValue(next);
        focusAt(next);
      }
    } else if (event.key === 'Home') {
      event.preventDefault();
      const next = ordered[0];
      if (next) {
        setValue(next);
        focusAt(next);
      }
    } else if (event.key === 'End') {
      event.preventDefault();
      const next = ordered[ordered.length - 1];
      if (next) {
        setValue(next);
        focusAt(next);
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented && !disabled) {
      setValue(value);
    }
  };

  const inner = (
    <>
      {typeof children === 'string' || typeof children === 'number' ? (
        <span
          style={{
            fontSize: size === 'lg' ? rem(16) : rem(12),
            lineHeight: size === 'lg' ? rem(22) : rem(14),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {children}
        </span>
      ) : (
        children
      )}
      {isSelected && (
        <motion.div
          className={`underline-slider ${size === 'md' ? 'underline-slider-small' : ''}`}
          layoutId={`underline-slider-${baseId}`}
        />
      )}
    </>
  );

  if (asChild) {
    return (
      <Slot
        ref={composeRefs(ref, (node: HTMLElement | null) => registry.setFocusEl(value, node))}
        role="tab"
        aria-selected={isSelected}
        aria-controls={panelId}
        tabIndex={isSelected ? 0 : -1}
        data-state={isSelected ? 'active' : 'inactive'}
        data-disabled={disabled ? 'true' : 'false'}
        onClick={handleClick as React.MouseEventHandler<HTMLElement>}
        onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLElement>}
        {...semanticProps}
        {...(rest as React.ComponentProps<typeof Slot>)}
        id={triggerId}
      >
        {children}
      </Slot>
    );
  }

  return (
    <StyledTab
      ref={composedRef}
      role="tab"
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={isSelected ? 0 : -1}
      data-state={isSelected ? 'active' : 'inactive'}
      data-disabled={disabled ? 'true' : 'false'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...semanticProps}
      {...rest}
      id={triggerId}
      $variant={variant}
      $size={size}
    >
      {inner}
    </StyledTab>
  );
});

TabsTrigger.displayName = 'Tabs.Trigger';

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  forceMount?: boolean;
  semanticId?: string;
}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(function TabsContent(
  { value: contentValue, forceMount = false, semanticId, children, hidden: hiddenProp, ...rest },
  ref,
) {
  const { value: selected, domIdPrefix } = useTabsContext();
  const semanticProps = getSemanticAttributes(semanticId);
  const active = selected === contentValue;
  const { triggerId, panelId } = compoundTriggerAndPanelIds(contentValue, domIdPrefix);

  if (!forceMount && !active) {
    return null;
  }

  if (forceMount && !active) {
    return (
      <TabContent
        ref={ref}
        role="tabpanel"
        aria-labelledby={triggerId}
        data-state="inactive"
        hidden
        {...semanticProps}
        {...rest}
        id={panelId}
      >
        {children}
      </TabContent>
    );
  }

  return (
    <motion.section
      key={contentValue}
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <TabContent
        ref={ref}
        role="tabpanel"
        aria-labelledby={triggerId}
        data-state="active"
        hidden={hiddenProp}
        {...semanticProps}
        {...rest}
        id={panelId}
      >
        {children}
      </TabContent>
    </motion.section>
  );
});

TabsContent.displayName = 'Tabs.Content';

/** Compound tabs: `Tabs.Root`, `Tabs.List`, `Tabs.Trigger`, `Tabs.Content`. */
export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};